import {createAction, createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
	AnchorEffectAssignValueEntry,
	EntityType,
	Entry,
	EntryProperty,
	PropertyDefinition,
	Repository, RepositoryEntry,
	TimelineCollectionEntry,
} from '../types';
import {RootState} from '../store';
import {buildReadableLabelMapForLocalEntries} from '../common/util';
import {recordsApi} from '../services/records';
import {queriesApi} from '../services/queries';
import { gridsApi } from '../services/grids';
import { anchorsApi } from '../services/anchors';
import { referencesApi } from '../services/references';
import { notesApi } from '../services/notes';
import {propertyDefinitionsApi} from '../services/propertyDefinitions';
import {repositoryEntriesApi} from '../services/repositoryEntries';
import { timelineCollectionsApi } from '../services/timelineCollections';
import { sharedTimelinesApi, SharedTimelineResponseDto } from '../services/sharedTimelines';
import { ENTRY_PROPERTY_TYPES, PROPERTY_DEFINITION_TYPES } from '../constants';

interface ContentState {
	locationId: string | null;
	entries: Entry[];
	isEntriesHydrated: boolean;
	clipboardEntries: Entry[];
	activeRepositoryId: string | null;
	/** Title shown in header when in repository or repository-entry context. Set by the active view. */
	repositoryHeaderTitle: string | null;
}

const initialState: ContentState = {
	locationId: null,
	entries: [],
	isEntriesHydrated: false,
	clipboardEntries: [],
	activeRepositoryId: null,
	repositoryHeaderTitle: null,
};

export const contentSlice = createSlice({
	name: 'content',
	initialState,
	reducers: {
		entriesSetState(state: ContentState, action) {
			state.entries = action.payload;
			return state;
		},
		entriesHydratedSetState(state: ContentState, action: PayloadAction<boolean>) {
			state.isEntriesHydrated = action.payload;
		},
		entriesUpdateMany(state: ContentState, action) {
			const updatesByIds = action.payload.reduce((acc: any, {id, ...update}: any) => acc.set(id, update), new Map());
			state.entries = state.entries.map((entry) => {
				const update = updatesByIds.get(entry.id);
				return !update ? entry : { ...entry, ...update };
			});
		},
		entriesAddMany(state: ContentState, action) {
			state.entries = [...state.entries, ...action.payload];
		},
		propertyDefinitionsAddMany(state: ContentState, action) {
			state.entries = [...state.entries, ...action.payload];
		},
		entriesAppendMany(state: ContentState, action) {
			const mergedEntries = [...state.entries, ...action.payload];
			const updatedEntriesMap = new Map(mergedEntries.map((entry) => [entry.id, entry]));
			state.entries = Array.from(updatedEntriesMap).map(([,entry]) => entry);
		},
		entriesDeleteMany(state: ContentState, action: PayloadAction<string[]>) {
			state.entries = state.entries.filter((entry) => !action.payload.includes(entry.id));
		},
		clipboardEntriesAddById(state: ContentState, action) {
			const clippedEntry = state.entries.find((entry) => action.payload === entry.id);
			state.clipboardEntries = clippedEntry ? [...state.clipboardEntries, clippedEntry] : state.clipboardEntries;
		},
		clipboardEntriesResetState(state: ContentState) {
			state.clipboardEntries = [];
		},
		locationResetState(state: ContentState, action: PayloadAction<string | null>) {
			state.locationId = action.payload;
			state.activeRepositoryId = null;
			state.repositoryHeaderTitle = null;
		},
		locationIdSetState(state: ContentState, action: PayloadAction<string | null>) {
			state.locationId = action.payload;
		},
		setActiveRepositoryId(state: ContentState, action: PayloadAction<string | null>) {
			state.activeRepositoryId = action.payload;
		},
		setRepositoryHeaderTitle(state: ContentState, action: PayloadAction<string | null>) {
			state.repositoryHeaderTitle = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(recordsApi.endpoints.loadEntry.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				const [locationEntry, ...childEntries] = action.payload;
				contentSlice.caseReducers.entriesAppendMany(state, contentSlice.actions.entriesAppendMany([...(locationEntry ? [locationEntry] : []), ...childEntries]));
			})
			.addMatcher(timelineCollectionsApi.endpoints.getTimelineCollections.matchFulfilled, (state: ContentState, action: PayloadAction<{ data: Entry[] }>) => {
				contentSlice.caseReducers.entriesAppendMany(state, contentSlice.actions.entriesAppendMany(action.payload.data));
			})
			.addMatcher(sharedTimelinesApi.endpoints.getSharedTimeline.matchFulfilled, (state: ContentState, action: PayloadAction<SharedTimelineResponseDto>) => {
				const { timeline, collections } = action.payload;
				contentSlice.caseReducers.entriesAppendMany(state, contentSlice.actions.entriesAppendMany([timeline, ...collections]));
			})
			.addMatcher(recordsApi.endpoints.entryCreate.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				const updatesByFakeIds = (action.payload as Entry[]).reduce((acc, syncedEntry) => {
					acc.set(syncedEntry.fakeId as string, syncedEntry);
					return acc;
				}, new Map<string, Entry>());
				state.entries = state.entries.map(entry => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(queriesApi.endpoints.queryCreate.matchFulfilled, (state: ContentState, action: PayloadAction<Entry>) => {
				const synced = action.payload as Entry;
				const updatesByFakeIds = new Map<string, Entry>([[synced.fakeId as string, synced]]);
				state.entries = state.entries.map((entry) => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(gridsApi.endpoints.gridCreate.matchFulfilled, (state: ContentState, action: PayloadAction<Entry>) => {
				const synced = action.payload as Entry;
				const updatesByFakeIds = new Map<string, Entry>([[synced.fakeId as string, synced]]);
				state.entries = state.entries.map((entry) => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(anchorsApi.endpoints.anchorCreate.matchFulfilled, (state: ContentState, action: PayloadAction<Entry>) => {
				const synced = action.payload as Entry;
				const updatesByFakeIds = new Map<string, Entry>([[synced.fakeId as string, synced]]);
				state.entries = state.entries.map((entry) => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(referencesApi.endpoints.createReferences.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				const updatesByFakeIds = (action.payload as Entry[]).reduce((acc, syncedEntry) => {
					acc.set(syncedEntry.fakeId as string, syncedEntry);
					return acc;
				}, new Map<string, Entry>());
				state.entries = state.entries.map(entry => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});	
			})
			.addMatcher(notesApi.endpoints.createNote.matchFulfilled, (state: ContentState, action: PayloadAction<Entry>) => {
				const synced = action.payload as Entry;
				const updatesByFakeIds = new Map<string, Entry>([[synced.fakeId as string, synced]]);
				state.entries = state.entries.map((entry) => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(propertyDefinitionsApi.endpoints.propertyDefinitionCreate.matchFulfilled, (state: ContentState, action: PayloadAction<Entry>) => {
				const synced = action.payload as Entry;
				const updatesByFakeIds = new Map([[synced.fakeId as string, synced]]);
				state.entries = state.entries.map(entry => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(recordsApi.endpoints.entryImport.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				const updatesByFakeIds = (action.payload as Entry[]).reduce((acc, syncedEntry) => {
					acc.set(syncedEntry.fakeId as string, syncedEntry);
					return acc;
				}, new Map<string, Entry>());
				state.entries = state.entries.map(entry => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(repositoryEntriesApi.endpoints.repositoryEntryCreate.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				const updatesByFakeIds = (action.payload as Entry[]).reduce((acc, syncedEntry) => {
					acc.set(syncedEntry.fakeId as string, syncedEntry);
					return acc;
				}, new Map<string, Entry>());
				state.entries = state.entries.map(entry => {
					const updatedEntry = updatesByFakeIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				});
			})
			.addMatcher(repositoryEntriesApi.endpoints.repositoryEntryUpdate.matchFulfilled, (state: ContentState, action: PayloadAction<{
				createdProperties: Entry[];
				updatedProperties: Entry[];
				deletedPropertyIds: string[];
			}>) => {
				const updatesByIds = (action.payload.updatedProperties as Entry[]).reduce((acc, updatedEntry) => {
					acc.set(updatedEntry.id as string, updatedEntry);
					return acc;
				}, new Map<string, Entry>());
				state.entries = [...state.entries.filter(entry => !action.payload.deletedPropertyIds.includes((entry.id))).map(entry => {
					const updatedEntry = updatesByIds.get(entry.id);
					return updatedEntry ? updatedEntry : entry;
				}), ...action.payload.createdProperties];
			})
			.addMatcher(recordsApi.endpoints.entryDelete.matchFulfilled, (state: ContentState, action: PayloadAction<{deletedIds: string[]}>) => {
				contentSlice.caseReducers.entriesDeleteMany(state, { ...action, payload: action.payload.deletedIds });
			})
			.addMatcher(repositoryEntriesApi.endpoints.repositoryEntryDelete.matchFulfilled, (state: ContentState, action: PayloadAction<{deletedIds: string[]}>) => {
				contentSlice.caseReducers.entriesDeleteMany(state, { ...action, payload: action.payload.deletedIds });
			})
			.addMatcher(propertyDefinitionsApi.endpoints.propertyDefinitionDelete.matchFulfilled, (state: ContentState, action: PayloadAction<{deletedIds: string[]}>) => {
				contentSlice.caseReducers.entriesDeleteMany(state, { ...action, payload: action.payload.deletedIds });
			})
			.addMatcher(repositoryEntriesApi.endpoints.repositoryEntryArchive.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				contentSlice.caseReducers.entriesUpdateMany(state, contentSlice.actions.entriesUpdateMany(action.payload));
			})
			.addMatcher(repositoryEntriesApi.endpoints.repositoryEntryUnarchive.matchFulfilled, (state: ContentState, action: PayloadAction<Entry[]>) => {
				contentSlice.caseReducers.entriesUpdateMany(state, contentSlice.actions.entriesUpdateMany(action.payload));
			});
	},
});

export const {
	entriesSetState,
	entriesHydratedSetState,
	entriesUpdateMany,
	entriesAddMany,
	propertyDefinitionsAddMany,
	entriesAppendMany,
	entriesDeleteMany,
	clipboardEntriesAddById,
	clipboardEntriesResetState,
	locationResetState,
	locationIdSetState,
	setActiveRepositoryId,
	setRepositoryHeaderTitle,
} = contentSlice.actions;

export const selectContentState = (state: RootState) => state.content;

export const selectLocationId = createSelector([selectContentState], (state) => state.locationId);

export const selectAllEntries = createSelector([selectContentState], (state) => state.entries);
export const selectIsEntriesHydrated = createSelector([selectContentState], (state) => state.isEntriesHydrated);

export const selectAllRepositoryEntries = createSelector([selectAllEntries], (allEntries) => allEntries.filter((entry) => entry.type === EntityType.RepositoryEntry) as RepositoryEntry[]);

export const selectAllEntryProperties = createSelector([selectAllEntries], (allEntries) => allEntries.filter((entry) => ENTRY_PROPERTY_TYPES.includes(entry.type as any)) as EntryProperty[]);

export const selectAllPropertyDefinitions = createSelector([selectAllEntries], (allEntries) => allEntries.filter((entry) => PROPERTY_DEFINITION_TYPES.includes(entry.type as any)) as PropertyDefinition[]);

export const selectAllTimelineCollections = createSelector([selectAllEntries], (allEntries) => allEntries.filter((entry) => entry.type === EntityType.TimelineCollection) as TimelineCollectionEntry[]);

export const selectAllAnchorEffects = createSelector([selectAllEntries], (allEntries) => allEntries.filter((entry) => entry.type === EntityType.AnchorEffectAssignValue) as AnchorEffectAssignValueEntry[]);

export const selectEntryReadableValues = createSelector([selectAllRepositoryEntries, selectAllPropertyDefinitions, selectAllEntryProperties], (allRepositoryEntries, allPropertyDefinitions, allEntryProperties) =>
	buildReadableLabelMapForLocalEntries(allRepositoryEntries, allPropertyDefinitions, allEntryProperties),
);

export const selectClipboardEntries = (state: RootState) => state.content.clipboardEntries;

export const selectClipboardEntriesSet = createSelector([selectClipboardEntries], (clipboardEntries) => new Set(clipboardEntries.map(({id}) => id)));

export const selectLocationEntryLocal = createSelector(
	[selectAllEntries, selectLocationId],
	(entries, locationId) => entries.find((entry) => entry.id === locationId || (!entry.companyId && entry.fakeId === locationId)) ?? null,
);

export const selectRepositoryHeaderTitle = createSelector([selectContentState], (state) => state.repositoryHeaderTitle);
export const selectActiveRepositoryId = createSelector([selectContentState], (state) => state.activeRepositoryId);

export const selectIsLocationEntryLoading = createSelector([], () => false);

export const selectIsLocationEntryLoaded = createSelector([selectLocationId, selectLocationEntryLocal], (locationId, locationEntryLocal) => !locationId || Boolean(locationEntryLocal));

export const selectLocationAllEntries = createSelector(
	[selectAllEntries, selectLocationId, selectClipboardEntriesSet],
	(entries, locationId, clipboardEntriesSet) =>
		entries.filter((entry) => entry.parentId === locationId && !clipboardEntriesSet.has(entry.id)),
);

export const selectAllRepositories = createSelector([selectAllEntries], (entries) => entries.filter((entry) => entry.type === EntityType.Repository) as Repository[]);

export const repositoryEntryUpdateLocalAction = createAction<any>('repositoryEntries/repositoryEntryUpdateLocal');

export const repositoryEntryArchiveLocalAction = createAction<any>('repositoryEntries/repositoryEntryArchiveLocal');

export const repositoryEntryUnarchiveLocalAction = createAction<any>('repositoryEntries/repositoryEntryUnarchiveLocal');

export const recordDeleteLocalAction = createAction<any>('records/recordDeleteLocal');

export default contentSlice.reducer;
